const IGNORED_TEAM_UPDATE_KEYS = [
  'last_seen_at',
  'owner',
  'updated_at',
  'last_published_at',
  'first_published_at'
] as const

export function shouldAutosaveTeamChange(args: {
  diff: Record<string, unknown> | null | undefined
  isApplyingRemoteTeam: boolean
}): boolean {
  if (args.isApplyingRemoteTeam) {
    return false
  }

  if (!args.diff) {
    return true
  }

  const diffKeys = Object.keys(args.diff)
  if (diffKeys.length === 0) {
    return true
  }

  return !diffKeys.every((key) => IGNORED_TEAM_UPDATE_KEYS.includes(key as (typeof IGNORED_TEAM_UPDATE_KEYS)[number]))
}

/** Every changed team field matches this; used when the change can't be narrowed down. */
export const ALL_TEAM_FIELDS = '*'

/**
 * The team fields a jsondiffpatch diff touches, as dot paths at most two levels
 * deep (`name`, `ai_context.audience`). A whole-object replacement or an
 * unknown diff touches everything.
 */
export function changedTeamPaths(diff: Record<string, unknown> | null | undefined): string[] {
  if (!diff || Array.isArray(diff)) {
    return [ALL_TEAM_FIELDS]
  }
  const paths: string[] = []
  for (const [key, change] of Object.entries(diff)) {
    if (IGNORED_TEAM_UPDATE_KEYS.includes(key as (typeof IGNORED_TEAM_UPDATE_KEYS)[number])) {
      continue
    }
    const isNestedObjectDiff =
      change !== null && typeof change === 'object' && !Array.isArray(change) && !('_t' in change)
    if (isNestedObjectDiff) {
      paths.push(...Object.keys(change).map((child) => `${key}.${child}`))
    } else {
      paths.push(key)
    }
  }
  return paths.length > 0 ? paths : [ALL_TEAM_FIELDS]
}

/** Whether two dot paths refer to the same field or one contains the other. */
export function pathsOverlap(a: string, b: string): boolean {
  return a === ALL_TEAM_FIELDS || b === ALL_TEAM_FIELDS || a === b || a.startsWith(`${b}.`) || b.startsWith(`${a}.`)
}
